/**
 * Milestone diamond markers + sequence captions at the top of each milestone line
 * (MapMilestoneMarker.vue). The diamond is a rounded square rotated 45°, coloured
 * by selected/mirrored/final/default; the caption is the sequence number centred
 * on the diamond (white, or black for mirrored). In status view
 * (`isHidingMilestoneColors`, unless selected) the diamond is filled by
 * state/status instead. WI #5094: state badge upper-right, overdue clock
 * lower-right. The project-start "S" marker is drawn by drawStartMarker.
 */
import { START_X } from '@/store/remapStore';
import { externalDateSync } from '@/utils/helpers/milestone/externalDateSync';
import { isOverdue, todayIsoDate } from '@/utils/helpers/overdue';
import { drawIcon, drawStateIcon } from '../../draw/icons';
import {
  cssVar,
  hexagonLabelColor,
  hexagonStatusFill,
  modeOf,
  overdueColor,
} from '../../draw/palette';
import { roundRect } from '../../draw/shapes';
import { withGroupOpacity } from '../../engine/groupOpacity';
import { makeMirroredStroke, type MirroredStroke } from './mirroredStroke';
import type { SceneContext } from '../../engine/SceneContext';
import type { MapMilestone } from '@/store/ioStore';

/**
 * WI #5622: compress-tool fix toggle (padlock chip) geometry, screen px. The
 * chip sits at the upper-right of the (intermediate) diamond, clear of the
 * external-date dot at its upper-left; the tooltip component reads the same
 * offsets to place its label.
 */
export const COMPRESS_FIX_TOGGLE_DX = 12;
export const COMPRESS_FIX_TOGGLE_Y = 109;
const COMPRESS_FIX_TOGGLE_RADIUS = 8;

// WI #5094: state badge and overdue clock glyph sizes, each on a white disc.
const STATE_BADGE_SIZE = 12;
const STATE_BADGE_RIM = 9.5;
const CLOCK_SIZE = 12;
const CLOCK_RIM = 7.5;

export function drawMilestoneMarkers(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  drawStartMarker(ctx, scene);
  // Source-project → mirror-outline colour, resolved once per frame.
  const mirroredStroke = makeMirroredStroke(scene);
  const today = todayIsoDate();
  // A dimmed (mirrored, out-of-focus) marker must flatten as a group — diamond +
  // mirror stroke + caption — before compositing at 0.2, else the black stroke
  // over the white diamond darkens. Draw normal markers straight to the layer and
  // the dimmed ones through one batched group-opacity pass.
  const dimmed: MapMilestone[] = [];
  for (const ms of io.milestones) {
    if (ms.isHiddenOnMap || map.isHiddenMirroredMilestone(ms.id)) {
      continue;
    }
    const x = scene.screenX(map.milestonePos(ms));
    if (x < -30 || x > scene.width + 30) {
      continue;
    }
    if (milestoneDim(scene, ms.id, !!ms.mirrored) < 1) {
      dimmed.push(ms);
    } else {
      drawMarker(ctx, scene, ms, mirroredStroke, today);
    }
  }
  if (dimmed.length) {
    withGroupOpacity(ctx, 0.2, (g) => {
      for (const ms of dimmed) {
        drawMarker(g, scene, ms, mirroredStroke, today);
      }
    });
  }
}

/**
 * Project-start "S" marker (retired SVG renderer's MapPastTime.vue:35 →
 * MapMilestoneMarker `start final
 * show-line caption="S"`): a vertical line + a blue (bluelight-600) final-size
 * diamond captioned "S" at the project origin (position 0). Synthetic — it is not
 * in io.milestones — and projects only (the SVG draws it in the past-time layer).
 * It carries no state/status, so status view never recolours it (the SVG
 * data-fill-mode rules match on data-state, which the start marker lacks).
 */
function drawStartMarker(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map } = scene;
  if (map.isTemplate) {
    return;
  }
  const x = scene.screenX(0);
  if (x < -30 || x > scene.width + 30) {
    return;
  }

  // Vertical line: final → gray-800, 2px, from y=150 down (MapMilestoneMarker
  // show-line; non-mirrored start, so y1=150 not the 130 of regular ms lines).
  ctx.save();
  ctx.lineWidth = 2;
  ctx.strokeStyle = cssVar('--color-gray-800', '#1D2939');
  ctx.beginPath();
  ctx.moveTo(x + 0.5, 150);
  ctx.lineTo(x + 0.5, scene.height);
  ctx.stroke();
  ctx.restore();

  // Diamond: final size 20, centre (screenX(0), 128), rotated 45°, bluelight-600.
  const size = 20;
  const half = size / 2;
  ctx.save();
  ctx.translate(x, 128);
  ctx.rotate(Math.PI / 4);
  roundRect(ctx, -half, -half, size, size, 3);
  ctx.fillStyle = cssVar('--color-bluelight-600', '#0086C9');
  ctx.fill();
  if (scene.ui.presentationMode === 'on') {
    // Projector-contrast outline (MapMilestoneMarker `[data-presentation='on']
    // .diamond:not(.mirrored)`).
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#000000';
    ctx.stroke();
  }
  ctx.restore();

  // Caption "S" (white, 800 12px, centred on the diamond).
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '800 12px Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('S', x, 133);
}

/** Draw a single milestone diamond + caption at full opacity. */
function drawMarker(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  ms: MapMilestone,
  mirroredStroke: MirroredStroke,
  today: string,
): void {
  const { map } = scene;
  const x = scene.screenX(map.milestonePos(ms));
  const final = ms.isFinal;
  const mirrored = !!ms.mirrored;
  const selected = map.isSelectedItem('milestone', ms.id);
  const statusView = map.isHidingMilestoneColors && !selected;
  const mode = modeOf(ms.state, ms.status);
  const size = final ? 20 : 17;
  const half = size / 2;
  // Diamond rect centre (SVG: rect x = scaleX + (final?0:2) − 10, y as below).
  const cx = x + (final ? 0 : 2) - 10 + half;
  const cy = (mirrored ? 140 : final ? 118 : 120) + half;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(Math.PI / 4);
  roundRect(ctx, -half, -half, size, size, 3);
  ctx.fillStyle = selected
    ? cssVar('--color-bluelight-500', '#0BA5EC')
    : statusView
      ? hexagonStatusFill(mode)
      : mirrored
        ? cssVar('--color-bluegray-0', '#FFFFFF')
        : final
          ? cssVar('--color-gray-800', '#1D2939')
          : cssVar('--color-gray-500', '#667085');
  ctx.fill();
  if (mirrored) {
    // Mirror outline in the source project's palette colour (MapMilestoneMarker
    // `milestoneStrokeColor`), black fallback — and black in status view, where the
    // SVG drops the inline stroke (the `.diamond.mirrored` CSS default applies).
    ctx.lineWidth = 2;
    ctx.strokeStyle = mirroredStroke(
      ms.sourceProjectId,
      map.isHidingMilestoneColors,
    );
    ctx.stroke();
  } else if (scene.ui.presentationMode === 'on') {
    // Presentation mode outlines non-mirrored diamonds for projector contrast
    // (MapMilestoneMarker `[data-presentation='on'] .diamond:not(.mirrored)`).
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#000000';
    ctx.stroke();
  }
  ctx.restore();

  ctx.fillStyle = statusView
    ? hexagonLabelColor(mode, undefined)
    : mirrored
      ? '#000000'
      : '#FFFFFF';
  ctx.font = '800 12px Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(String(ms.sequence), x, mirrored ? 153 : 133);

  // WI #5094: clickable state badge; the padlock chip and the overview overlay use that spot.
  const bx = cx + half + 1;
  const by = cy - half - 1;
  if (
    !map.isTemplate &&
    !mirrored &&
    !map.isCompressingPhase &&
    !map.isMilestoneOverlay &&
    bx - STATE_BADGE_RIM >= START_X
  ) {
    ctx.beginPath();
    ctx.arc(bx, by, STATE_BADGE_RIM, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    drawStateIcon(ctx, ms.state, ms.status, bx, by, STATE_BADGE_SIZE);
    scene.hits.addMilestoneStateIcon(ms.id, bx, by);
  }

  if (!map.isTemplate && !mirrored && isOverdue(ms.deadline, ms.state, today)) {
    const kx = cx + half + 4;
    const ky = cy + half + 2;
    ctx.beginPath();
    ctx.arc(kx, ky, CLOCK_RIM, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    drawIcon(ctx, 'Clock', kx, ky, CLOCK_SIZE, overdueColor());
  }

  // External-date-sync status dot (MapMilestoneMarker `external-date-dot`,
  // MapMilestoneItem:66-71,100): a small r4 dot at the diamond's upper-left,
  // coloured by sync state (in-sync success / out-of-sync bluelight-700 —
  // darker than the bluelight-500 selected-diamond fill so the dot stays
  // visible on a selected milestone; colours mirror the
  // external-date-dot-colors mixin in helpers.scss), white stroke. Only when
  // the milestone has an external date.
  const sync = externalDateSync(ms.deadline, ms.externalDate);
  if (sync) {
    ctx.beginPath();
    ctx.arc(x + (final ? -10 : -8), final ? 115 : 117, 4, 0, Math.PI * 2);
    ctx.fillStyle =
      sync === 'in-sync'
        ? cssVar('--color-success-500', '#12B76A')
        : cssVar('--color-bluelight-700', '#026AA2');
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#FFFFFF';
    ctx.stroke();
  }

  // WI #5622 — compress-tool fix toggle: a padlock chip on every fixable
  // intermediate milestone while the compress tool is armed. During the drag
  // only the engaged (fixed) chips stay, so the wall the user is pushing
  // against remains in view while the map stays quiet. Never in the vector
  // export.
  if (
    map.isCompressingPhase &&
    !scene.exporting &&
    map.isCompressFixableMilestone(ms)
  ) {
    const fixed = map.isCompressFixedMilestone(ms.id);
    if (fixed || !map.compressToolAnchor) {
      drawCompressFixToggle(ctx, scene, ms, x, fixed);
    }
  }
}

/**
 * WI #5622: the padlock chip — a white (hovered: gray-100) disc with an open
 * padlock while fixable, a bluelight-600 disc with a closed white padlock once
 * fixed. Records its centre in the HitRegistry so the interaction router can
 * resolve the click.
 */
function drawCompressFixToggle(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  ms: MapMilestone,
  x: number,
  fixed: boolean,
): void {
  const cx = x + COMPRESS_FIX_TOGGLE_DX;
  const cy = COMPRESS_FIX_TOGGLE_Y;
  // Under the workstream sidebar: neither visible nor clickable.
  if (cx - COMPRESS_FIX_TOGGLE_RADIUS < START_X) {
    return;
  }
  const hovered = scene.map.hoveredCompressFixMilestoneId === ms.id;

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, COMPRESS_FIX_TOGGLE_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = fixed
    ? cssVar('--color-bluelight-600', '#0086C9')
    : hovered
      ? cssVar('--color-gray-100', '#F2F4F7')
      : '#FFFFFF';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = fixed
    ? cssVar('--color-bluelight-700', '#026AA2')
    : cssVar('--color-gray-400', '#98A2B3');
  ctx.stroke();
  ctx.restore();

  drawIcon(
    ctx,
    fixed ? 'Lock' : 'LockOpen',
    cx,
    cy,
    10,
    fixed ? '#FFFFFF' : cssVar('--color-gray-600', '#475467'),
  );
  scene.hits.addCompressFixToggle(ms.id, cx, cy);
}

/**
 * Filter dimming for a milestone (SVG MapMilestoneItem `.shadowed`): only mirrored
 * milestones dim, and only while a project filter is active and this one isn't in
 * the focused set. Returns 0.2 when dimmed, else 1.
 */
export function milestoneDim(
  scene: SceneContext,
  milestoneId: string,
  mirrored: boolean,
): number {
  const { map } = scene;

  return mirrored &&
    map.focusedProjects.length > 0 &&
    map.isShadowedMilestoneId(milestoneId)
    ? 0.2
    : 1;
}
